import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useAppStore } from '../../state/store';

/**
 * Sketch sky dome — the Settings › Sketch "Sky Gradient" background.
 *
 * A camera-locked inverted sphere painted by a direction-based shader,
 * mirroring the SketchUp background style: above the horizon the horizon
 * color blends into the sky color toward the zenith, below it the ground
 * color fills the lower hemisphere (Z-up world — the XY plane is the
 * horizon line). The dome is drawn first and never reads or writes depth,
 * so the whole scene composes over it, and the raw shader ignores the
 * renderer's global clipping planes — a section cut opens the model against
 * the sky instead of punching a hole in it.
 *
 * Disabled state renders nothing and the flat `bg.canvas.sketch` canvas from
 * the viewport shell stays visible (the pre-gradient look).
 */

const DOME_RADIUS = 5000; // comfortably inside the workbench camera far planes (10000)

const VERT_SHADER = /* glsl */ `
varying vec3 vDir;
void main() {
    // The dome never rotates, so the normalized object-space position of a
    // vertex IS its direction from the dome center (= the camera).
    vDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG_SHADER = /* glsl */ `
uniform vec3 uSkyColor;
uniform vec3 uHorizonColor;
uniform vec3 uGroundColor;
varying vec3 vDir;
void main() {
    // Z-up world: +z is the zenith, h <= 0 is below the horizon.
    float h = vDir.z;
    // Gentle SketchUp-like easing: the sky color arrives well before the
    // zenith so the gradient reads from almost any camera pitch.
    float t = pow(max(h, 0.0), 0.65);
    vec3 sky = mix(uHorizonColor, uSkyColor, t);
    // Thin blend band across the horizon line so the seam never aliases.
    float blend = smoothstep(-0.015, 0.0, h);
    gl_FragColor = vec4(mix(uGroundColor, sky, blend), 1.0);
    #include <colorspace_fragment>
}
`;

export function SketchSky() {
    const enabled = useAppStore((s) => s.sketchOptions.skyGradient);
    const skyColor = useAppStore((s) => s.sketchOptions.skyColor);
    const horizonColor = useAppStore((s) => s.sketchOptions.horizonColor);
    const groundColor = useAppStore((s) => s.sketchOptions.groundColor);
    const camera = useThree((s) => s.camera);
    const domeRef = useRef<THREE.Mesh>(null);

    const material = useMemo(
        () =>
            new THREE.ShaderMaterial({
                vertexShader: VERT_SHADER,
                fragmentShader: FRAG_SHADER,
                uniforms: {
                    uSkyColor: { value: new THREE.Color('#ffffff') },
                    uHorizonColor: { value: new THREE.Color('#ffffff') },
                    uGroundColor: { value: new THREE.Color('#ffffff') },
                },
                side: THREE.BackSide,
                // Painted first, never occludes: every scene object composes
                // over the dome regardless of its own depth.
                depthWrite: false,
                depthTest: false,
                fog: false,
            }),
        []
    );

    // The material was built imperatively — release it on unmount.
    useEffect(() => () => material.dispose(), [material]);

    // Live color edits (Settings › Sketch pickers) repaint the dome.
    useEffect(() => {
        (material.uniforms.uSkyColor.value as THREE.Color).set(skyColor);
        (material.uniforms.uHorizonColor.value as THREE.Color).set(horizonColor);
        (material.uniforms.uGroundColor.value as THREE.Color).set(groundColor);
    }, [material, skyColor, horizonColor, groundColor]);

    // Camera-locked dome: it can never be outgrown by zooming and the
    // horizon line stays at eye level, exactly like SketchUp's background.
    useFrame(() => {
        domeRef.current?.position.copy(camera.position);
    });

    if (!enabled) return null;

    return (
        <mesh
            ref={domeRef}
            material={material}
            renderOrder={-1}
            frustumCulled={false}
            scale={DOME_RADIUS}
        >
            <sphereGeometry args={[1, 48, 24]} />
        </mesh>
    );
}
